// @ts-check
import React, { Component } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect, Dispatch } from 'react-redux'; // eslint-disable-line no-unused-vars
import api from 'utils/api';
import removeEmptyKeys from 'utils/removeEmptyKeys';
import { decamelizeKeys } from 'humps';
import {
  flashSuccess,
  flashError,
} from 'shared/actions/UnboundFlashMessageActions';
import { PageTitle, Row, Widget } from 'shared/containers/portal_layout';
import ReferralConfirmationForm from './ReferralConfirmationForm';
import LoadingContent from 'shared/components/LoadingContent';
import ReferralDetails from './ReferralDetails';
import DrivingDirections from './DrivingDirections';
import {
  referralTypeForQueryParams,
  urlBuilder,
  ensureLocationInvariant,
} from './ReferralConfirmationHelpers';
import { ReferralNewData, ReferralConfirmationFormType } from './types'; // eslint-disable-line no-unused-vars

/** @typedef {{
  outOfGroupReason?: string,
  saveReferral: (formValues: ReferralConfirmationFormType) => Promise
} & ReferralNewData} PageContentPropsType */

/** @type { React.ValidationMap<PageContentPropsType> } */
const pageContentPropTypes = {
  provider: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  directions: PropTypes.object,
  endAddress: PropTypes.object,
  appointmentSlot: PropTypes.object,
  instructions: PropTypes.string,
  mapUrl: PropTypes.string,
  referral: PropTypes.object.isRequired,
  referralProfile: PropTypes.object.isRequired,
  specialtyName: PropTypes.string.isRequired,
  loyaltyPrompt: PropTypes.bool.isRequired,
  outOfGroupReason: PropTypes.string,
  saveReferral: PropTypes.func.isRequired,
};

/** @type { React.FC<PageContentPropsType> } */
const PageContent = (props) => {
  const {
    provider,
    location,
    directions,
    endAddress,
    appointmentSlot,
    instructions: patientInstructions,
    mapUrl,
    referral,
    referralProfile,
    specialtyName,
    loyaltyPrompt,
    outOfGroupReason,
    collectLast4Ssn,
    saveReferral,
  } = props;

  return (
    <div>
      <Row>
        <Widget className="nopadding" title="Appointment Details" span={6}>
          <ReferralDetails
            provider={provider}
            appointmentSlot={appointmentSlot}
            specialtyName={specialtyName}
            location={location}
            endAddress={endAddress}
            patientInstructions={patientInstructions}
          />
        </Widget>

        <Widget
          className="nopadding"
          title={`Final Info for ${referralProfile.patientName}`}
          span={6}
        >
          <ReferralConfirmationForm
            form="referralConfirmation"
            loyaltyPrompt={loyaltyPrompt}
            collectLast4Ssn={collectLast4Ssn}
            onSubmit={saveReferral}
            initialValues={{ ...referral, outOfGroupReason }}
            referralProfile={referralProfile}
            referral={referral}
          />
        </Widget>
      </Row>

      {endAddress && (
        <Row>
          {!!mapUrl && (
            <Widget title="Map" span={6}>
              <img src={mapUrl} alt="map" />
            </Widget>
          )}
          <Widget title="Directions to Appointment" span={6}>
            {directions ? (
              <DrivingDirections directions={directions} />
            ) : (
              <p>Unavailable</p>
            )}
          </Widget>
        </Row>
      )}
    </div>
  );
};

PageContent.displayName = 'PageContent';
PageContent.propTypes = pageContentPropTypes;

/** @typedef {{
  apiPrefix: string,
  webpageBase: string,
  outOfGroupReason?: string,
  match: any,
  location: any, // NB: `react-router` "location", not Preferral "location"
  history: any,
  dispatch: Dispatch<any>
}} ReferralConfirmationPagePropsType */

/** @typedef {{
  fetched: boolean,
  apiData: ReferralNewData | void
}} PageStateType */

/** @type { React.ValidationMap<ReferralConfirmationPagePropsType> } */
const propTypes = {
  apiPrefix: PropTypes.string.isRequired,
  webpageBase: PropTypes.string.isRequired,
  outOfGroupReason: PropTypes.string,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

/** @augments { Component<ReferralConfirmationPagePropsType, PageStateType> } */
class ReferralConfirmationPage extends Component {
  constructor(props) {
    super(props);
    this.state = { fetched: false, apiData: null };
    this.fetchReferralProfile = this.fetchReferralProfile.bind(this);
    this.saveReferral = this.saveReferral.bind(this);
  }

  componentDidMount() {
    this.fetchReferralProfile();
  }

  fetchReferralProfile() {
    const {
      apiPrefix,
      location: { query },
      match,
    } = this.props;
    const url = `${apiPrefix}/referral_profiles/${match.params.referralProfileId}/referrals/new`;
    ensureLocationInvariant(query);
    const params = decamelizeKeys(query);
    return api.get(url, { params }).then((response) => {
      this.setState({ fetched: true, apiData: response.data });
    });
  }

  /**
   * @param {ReferralConfirmationFormType} formValues
   * @returns {Promise}
   */
  saveReferral(formValues) {
    const { apiPrefix, webpageBase, match, location, history, dispatch } =
      this.props;
    const {
      appointmentSlotId,
      providerMappingId,
      locationId,
      organizationId,
      specialtyId,
    } = location.query;
    const referralType = referralTypeForQueryParams(location.query);
    const url = urlBuilder.forPost[referralType](
      apiPrefix,
      match.params.referralProfileId
    );

    const referralData = decamelizeKeys(formValues);
    // HACK:
    if (referralData.last4_ssn !== undefined) {
      referralData.last_4_ssn = referralData.last4_ssn;
      delete referralData.last4_ssn;
    }

    return api
      .postWithoutDefaultRejectionHandling(
        url,
        removeEmptyKeys({
          referral_attributes: referralData,
          appointment_slot_id: appointmentSlotId,
          provider_mapping_id: providerMappingId,
          location_id: locationId,
          organization_id: organizationId,
          specialty_id: specialtyId,
        })
      )
      .then(
        (response) => {
          dispatch(
            flashSuccess({
              message: 'You have successfully created a referral',
            })
          );
          history.push(`${webpageBase}/referrals/${response.data.id}`);
          return null;
        },
        (error) => {
          if (
            error.response &&
            error.response.status === 422 &&
            error.response.data.errorCode === 'appointment_slot_taken'
          ) {
            dispatch(
              flashError({
                message:
                  "I'm sorry, that appointment slot is no longer available. Please choose another slot",
              })
            );
            history.push(
              `${webpageBase}/referral_profiles/${match.params.referralProfileId}/referral_dashboard`
            );
            return null;
          }
          return error.defaultRejectionHandler(error);
        }
      );
  }

  render() {
    return (
      <PageTitle title="Confirm Referral">
        {this.state.fetched ? (
          <PageContent
            {...this.state.apiData}
            outOfGroupReason={this.props.outOfGroupReason}
            saveReferral={this.saveReferral}
          />
        ) : (
          <LoadingContent />
        )}
      </PageTitle>
    );
  }
}

ReferralConfirmationPage.propTypes = propTypes;

const mapStateToProps = (state) => {
  return {
    apiPrefix: state.configuration.apiPrefix,
    webpageBase: state.configuration.webpageBase,
    outOfGroupReason: state.referralDashboardResults.pageState.outOfGroupReason,
  };
};

export default connect(mapStateToProps)(ReferralConfirmationPage);
